/**
 * Re-probes the API when the app returns to the foreground.
 *
 * Mobile browsers suspend the health-check interval while the tab is in the
 * background, and an in-flight ping may fail. No `online` event fires when the
 * app comes back (the network did not change), so the "server is not responding"
 * state would stay until a manual refresh. Forcing an immediate probe clears it.
 */

export interface ForegroundProbeTarget {
  handlers: { onNetworkChanged: (online: boolean) => void };
  forceHealthCheck: () => Promise<void>;
}

/** Listens for app switch, bfcache restore and window focus. Returns the detach function. */
export function attachForegroundReprobe(target: ForegroundProbeTarget): () => void {
  const handleForeground = () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    target.handlers.onNetworkChanged(navigator.onLine);
    void target.forceHealthCheck();
  };

  document.addEventListener("visibilitychange", handleForeground);
  globalThis.addEventListener("pageshow", handleForeground);
  globalThis.addEventListener("focus", handleForeground);

  return () => {
    document.removeEventListener("visibilitychange", handleForeground);
    globalThis.removeEventListener("pageshow", handleForeground);
    globalThis.removeEventListener("focus", handleForeground);
  };
}
